"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, SlidersHorizontal as Sliders, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, UserCheck, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processSteps = [{
  id: "step-1",
  stepNumber: "01",
  title: "Comprehensive SEO Audit",
  subtitle: "Uncovering technical blockers & growth opportunities",
  description: "I personally analyze your website's technical health, site architecture, and existing content to identify exact bottlenecks preventing higher rankings.",
  deliverable: "Custom SEO Audit & Strategic Action Plan",
  icon: Search
}, {
  id: "step-2",
  stepNumber: "02",
  title: "On-Page & Content Optimization",
  subtitle: "Aligning architecture with target search intent",
  description: "I optimize core landing pages, refine internal linking, and enhance your key copy to align directly with high-converting search queries.",
  deliverable: "Fully Optimized High-Intent Landing Pages",
  icon: Sliders
}, {
  id: "step-3",
  stepNumber: "03",
  title: "Citations & Local Authority",
  subtitle: "Establishing trust across authoritative directories",
  description: "I audit, clean, and build geographic and industry-specific citations to solidify your presence in local and national search ecosystems.",
  deliverable: "Verified Directory & Map Citations",
  icon: MapPin
}, {
  id: "step-4",
  stepNumber: "04",
  title: "Reputation & Review Integration",
  subtitle: "Leveraging authentic social proof for organic conversions",
  description: "I implement review collection strategies and trust signal structures so search algorithms and real users recognize your business as an industry authority.",
  deliverable: "Review Capture Strategy & Trust Badges",
  icon: Star
}, {
  id: "step-5",
  stepNumber: "05",
  title: "Structured Schema & AI Search",
  subtitle: "Preparing your site for modern AI search engines",
  description: "I craft tailored JSON-LD schema markup to help Google, ChatGPT, and AI-driven search engines parse and feature your brand's core offerings.",
  deliverable: "Custom Entity & Service Schema Code",
  icon: Code2
}, {
  id: "step-6",
  stepNumber: "06",
  title: "Transparent Reporting & Iteration",
  subtitle: "Measuring ranking gains, lead quality, and organic ROI",
  description: "You receive clear, direct monthly reports tracking real keyword movements and organic conversions—with regular strategic check-ins directly with me.",
  deliverable: "Monthly ROI & Executive Performance Reports",
  icon: BarChart3
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultancyprocessflow" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-secondary/30 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-accent/10 rounded-full blur-2xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs sm:text-sm font-medium rounded-full bg-secondary text-secondary-foreground border border-border">
            <Sparkles className="size-3.5 mr-1.5 text-accent" />
            End-To-End SEO Process
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            A Clear, Hands-On Roadmap to Sustainable Search Visibility
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            When you hire me, you work directly with Yassine Essaadi—not a junior account manager. Here is the step-by-step methodology I use to audit, optimize, and scale organic performance for my clients.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-foreground text-xs sm:text-sm font-medium border border-border">
            <UserCheck className="size-4 text-primary shrink-0" />
            <span>Direct Consultant Access • Tailored Strategy • No Agency Middlemen</span>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 relative">
          {processSteps.map((step, i) => {
          const IconComponent = step.icon;
          const isLast = i === processSteps.length - 1;
          return <motion.div key={step.id} data-index={i} variants={itemVariants} className="relative group h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 rounded-2xl flex flex-col justify-between overflow-hidden">
                  <div>
                    <CardHeader className="pt-6 px-6 pb-2">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg shadow-sm">
                          {step.stepNumber}
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground border border-accent/20">
                            Step {i + 1} of 6
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-wider mb-1">
                        <IconComponent className="size-4 shrink-0" />
                        <span>Phase {step.stepNumber}</span>
                      </div>

                      <CardTitle className="text-xl font-semibold text-card-foreground font-default leading-snug">
                        {step.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 pb-6 pt-2">
                      <p className="text-xs font-medium text-accent mb-3">
                        {step.subtitle}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed font-default mb-6">
                        {step.description}
                      </p>

                      <div className="pt-4 border-t border-border flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-xs font-medium text-foreground">
                          <strong className="text-muted-foreground font-normal">Deliverable: </strong>
                          {step.deliverable}
                        </span>
                      </div>
                    </CardContent>
                  </div>
                </Card>

                {!isLast && <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                    <div className="size-2 rounded-full bg-accent/40 ring-4 ring-background" />
                  </div>}
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 lg:mt-20 rounded-2xl bg-muted/60 border border-border p-8 lg:p-10 text-center max-w-4xl mx-auto shadow-sm">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-secondary text-accent mb-4">
            <Sparkles className="size-6" />
          </div>

          <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-3">
            Ready to Begin Your Customized SEO Growth Roadmap?
          </h3>

          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-8 font-default">
            Book a complimentary 30-minute consultation. I will review your website live, discuss your business objectives, and outline practical steps to improve your search performance.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="w-full sm:w-auto font-medium rounded-xl text-base px-6 py-6" asChild>
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium rounded-xl text-base px-6 py-6" asChild>
              <Link to="/CaseStudiesPage">
                <span>Explore My Case Studies</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}